import React, { useEffect, useState } from "react";
import {
  Avatar,
  Grid,
  List,
  Paper,
  Stack,
  Typography,
  Box,
  Divider, useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { userSelector } from "../user-redux/selector";
import Key from "../../clientVariables.json";
import moment from "moment";
import CandidateDashboardListSkeleton from "./CandidateDashboardListSkeleton";

const Candidatedashboardinterviews = ({setInterviewCount}) => {
  const { user } = useSelector(userSelector);
  const [candidateSubmissions, setCandidateSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  // Define breakpoints for different screen sizes
  const isMobileSmall = useMediaQuery(theme.breakpoints.down("xs")); // Very small mobile devices
  const isMobile = useMediaQuery(theme.breakpoints.between("xs", "sm")); // Regular mobile devices
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablets
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // Desktops and larger
  const isDesktopSmall = useMediaQuery(theme.breakpoints.between("md", "lg")); // Small desktops
  const isDesktopLarge = useMediaQuery(theme.breakpoints.up("lg")); // Large desktops


  useEffect(() => {
    getSubmissionsCandidate();
  }, []);

  const isUpcomingInterview = (job) => {
    const isNotCompleted =
      job.status !== "Selected" && job.status !== "Rejected";
    const isUpcoming = moment(job.interview_date).isSameOrAfter(moment(), "day");
    return isNotCompleted && isUpcoming;
  };

  const getSubmissionsCandidate = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${Key.domain}/candidate/${user?.candidate_id}/scheduled_interviews`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await response.json();
      const interviews = data?.data?.interviews || [];
      setCandidateSubmissions(interviews);

      const upcomingCount = interviews.filter(isUpcomingInterview).length;
      if (typeof setInterviewCount === "function") {
        setInterviewCount(upcomingCount);
      }
    } catch (error) {
      console.error("Error fetching interviews:", error);
      setCandidateSubmissions([]);
      if (typeof setInterviewCount === "function") {
        setInterviewCount(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const upcomingInterviews = candidateSubmissions
    .filter(isUpcomingInterview)
    .sort((a, b) => moment(a.interview_date).diff(moment(b.interview_date)))
    .slice(0, 4);

  return (
    <Box sx={{ padding: "16px", backgroundColor: "#f4f6f8" }}>
      {loading ? (
        <CandidateDashboardListSkeleton />
      ) : upcomingInterviews.length > 0 ? (
        <Grid container spacing={2}>
          {upcomingInterviews.map((job, index) => (
              <Grid item xs={12} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #ddd",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    padding: "13px",
                    transition: "transform 0.3s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Grid container alignItems="center">
                    {/* Avatar Section */}
                    {/* {console.log("Job:", job)} */}
                    <Grid
                      item
                      xs={2}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Avatar
                        alt={job.jobtitle || "Job"}
                        sx={{
                          height: isDesktopSmall
                            ? "40px"
                            : isMobileSmall ?
                              "10px" : isMobile ? "35px" : isTablet ?
                                "50px" : "50px",
                          width: isDesktopSmall ? "40px" : isMobileSmall ? "10px" : isMobile ? "35px" : isTablet ? "50px" : "50px",
                          fontSize: isMobileSmall ? "12px" : "20px",
                        }}
                      >
                        {job.jobtitle ? job.jobtitle[0] : "J"}
                      </Avatar>
                    </Grid>

                    {/* Job Details Section */}
                    <Grid item xs={7}>
                      <Stack spacing={0.5}>
                        {/* Job Title */}
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "bold",
                            fontSize: isMobileSmall
                              ? "10px" // Smaller font for very small mobile devices
                              : isMobile || isDesktopSmall
                                ? "10px" // Slightly larger font for regular mobile devices
                                : "15px", // Default size for larger devices

                            color: "#333333",
                            lineHeight: "1.2",
                          }}
                        >
                          {job.jobtitle?.length > 30
                            ? `${job.jobtitle.slice(0, 25)}...`
                            : job.jobtitle}
                        </Typography>

                        {/* Client Name */}
                        <Typography
                          variant="body2"
                          sx={{ color: "#666666", fontSize: "13px" }}
                        >
                          {job.client_name || "N/A"}
                        </Typography>

                        {/* Divider */}
                        <Divider sx={{ marginY: "4px" }} />

                        {/* Interview Type */}
                        <Typography
                          variant="body2"
                          sx={{ color: "#888888", fontSize: "12px" }}
                        >
                          {job.status_value || "Interview Scheduled"}



                        </Typography>
                      </Stack>
                    </Grid>

                    {/* Date Section */}
                    <Grid item xs={3}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          backgroundColor: "#fefefe",
                          padding: "12px 0",
                          boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                          width: "100px",
                          margin: "0 auto",
                        }}
                      >
                        {/* Day */}
                        <Typography
                          variant="h4"
                          sx={{ color: "#2d9596", fontWeight: "bold", fontSize: '16px' }}
                        >
                          {moment(job.interview_date).format("D")}
                        </Typography>

                        {/* Month */}
                        <Typography
                          variant="body1"
                          sx={{
                            backgroundColor: "#ff4f4f",
                            color: "#fff",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontWeight: "600",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                            fontSize: "12px",
                          }}
                        >
                          {moment(job.interview_date).format("MMM")}, {moment(job.interview_date).format("YYYY")}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#888888", fontSize: "12px" }}
                        >{job.interview_time}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
          ))}
        </Grid>
      ) : (
        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            marginTop: "20px",
            color: "#666666",
            fontSize: "16px",
          }}
        >
          No Interviews found
        </Typography>
      )}
    </Box>
  );
};

export default Candidatedashboardinterviews;
